import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Sms as SmsIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as FailedIcon,
  Schedule as PendingIcon,
} from "@mui/icons-material";

// API imports
import SmsReportsApiService from "../../../../infrastructure/api/smsReports";

const MySmsHistory = () => {
  const [loading, setLoading] = useState(false);
  const [smsList, setSmsList] = useState([]);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadSmsHistory();
  }, [pageNumber]);

  const loadSmsHistory = async () => {
    if (pageNumber === 1) setLoading(true);
    setError(null);

    try {
      const response = await SmsReportsApiService.getMySmsHistory(pageNumber, 20);
      if (response.isSuccess) {
        const newSmsList = response.data || [];
        if (pageNumber === 1) {
          setSmsList(newSmsList);
        } else {
          setSmsList(prev => [...prev, ...newSmsList]);
        }
        setHasMore(newSmsList.length === 20);
      } else {
        setError(response.message || "خطا در دریافت تاریخچه SMS");
      }
    } catch (error) {
      setError("خطا در ارتباط با سرور");
      console.error("Error loading SMS history:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setPageNumber(prev => prev + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("fa-IR");
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
      case "successful":
        return <SuccessIcon color="success" />;
      case "failed":
        return <FailedIcon color="error" />;
      case "pending":
        return <PendingIcon color="warning" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
      case "successful":
        return "success";
      case "failed":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Typography color="text.secondary">پروفایل</Typography>
        <Typography color="text.primary">تاریخچه پیامک‌ها</Typography>
      </Breadcrumbs>

      {/* SMS History */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <SmsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              تاریخچه پیامک‌های دریافتی ({smsList.length} مورد)
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {smsList.length === 0 && !loading ? (
            <Box textAlign="center" py={4}>
              <SmsIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                هیچ پیامکی یافت نشد
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>نوع پیامک</TableCell>
                      <TableCell>وضعیت</TableCell>
                      <TableCell>تاریخ ارسال</TableCell>
                      <TableCell>جزئیات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {smsList.map((sms) => (
                      <TableRow key={sms.id}>
                        <TableCell>
                          <Chip
                            label={sms.smsType}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(sms.sendStatusTitle)}
                            <Chip
                              label={sms.sendStatusTitle}
                              color={getStatusColor(sms.sendStatusTitle)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(sms.createDate)}</TableCell>
                        <TableCell>
                          <Accordion sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ minHeight: 48, px: 0 }}
                            >
                              <Typography variant="body2">مشاهده متن</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 0 }}>
                              <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                  {sms.message}
                                </Typography>
                                {sms.requestTitle && (
                                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: "block" }}>
                                    مرتبط با: {sms.requestTitle}
                                  </Typography>
                                )}
                              </Paper>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {loading && (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {hasMore && !loading && (
                <Box textAlign="center" mt={2}>
                  <Button variant="outlined" onClick={loadMore}>
                    بارگذاری بیشتر
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MySmsHistory;